import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { EntitlementRow } from "./useRevokeEntitlement";

function stateBadge(status: string, expiresAt: string | null) {
  if (status === "revoked") {
    return <Badge variant="outline">Revoked</Badge>;
  }
  if (expiresAt && new Date(expiresAt) <= new Date()) {
    return <Badge variant="secondary">Expired</Badge>;
  }
  return <Badge className="border-green-200 bg-green-100 text-green-800">Active</Badge>;
}

interface PaidAccessEntitlementRowsProps {
  rows: EntitlementRow[];
  revokingId: string | null;
  onRevoke: (row: EntitlementRow) => void;
  showResourceTitle?: boolean;
}

export function PaidAccessEntitlementRows({
  rows,
  revokingId,
  onRevoke,
  showResourceTitle = true,
}: PaidAccessEntitlementRowsProps) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No entitlements for this filter.</p>;
  }

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const name =
          [row.user.firstName, row.user.lastName].filter(Boolean).join(" ") || row.user.email;
        const expiresAt = row.expiresAt ? String(row.expiresAt) : null;
        return (
          <Card key={row.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              {showResourceTitle && <p className="font-medium">{row.digitalResource.title}</p>}
              <p className="text-sm text-muted-foreground">{name}</p>
              <p className="text-xs text-muted-foreground">
                Granted {new Date(row.grantedAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {stateBadge(row.status, expiresAt)}
              {row.invoiceId ? (
                <Link
                  className="text-sm text-primary hover:underline"
                  to={`/admin/invoices/${row.invoiceId}/receipt`}
                >
                  Receipt
                </Link>
              ) : null}
              {row.status === "active" && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={revokingId === row.id}
                  onClick={() => onRevoke(row)}
                >
                  Revoke
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
